import { useMemo } from 'react';
import moment, { Moment } from 'moment';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useRoomSettings } from '@features/roomSettings';
import { findNextAvailableStay, type NextAvailableStay } from './findNextAvailableStay';
import { useRoomNextAvailableCalendar } from './queries';

const DATE_FORMAT = 'YYYY-MM-DD';
const MONTHS_AHEAD = 2;

type Params = {
  roomCode: string;
  fromDate?: Moment;
  nights: number;
  adults: number;
  children: number;
  isUnavailable: boolean;
};

/**
 * The next bookable stay for one room, or null.
 *
 * Only runs when the room is actually unavailable, so the extra request lands on a page that has
 * nothing else to show. Gated on the site's provider and calendar-pricing configuration, which the
 * API resolves into `nextAvailableDatesEnabled`, and on a feature flag for a deploy-free kill
 * switch. A null result means "say nothing" — never "there is no availability".
 *
 * Callers must exclude the basket-constrained case: a room whose units are all already in the
 * basket is bookable on the selected dates, so suggesting other dates would be wrong. Calendar
 * availability is per-night and boolean, so a suggested stay is best-effort on room quantity;
 * selecting it re-runs the real search, which is what decides.
 */
export function useNextAvailableStay({
  roomCode,
  fromDate,
  nights,
  adults,
  children,
  isUnavailable,
}: Params): { nextStay: NextAvailableStay | null; isLoading: boolean } {
  const isFlagOn = useFeatureIsOn('feature.rooms.next-available-dates');
  const { data: roomSettings } = useRoomSettings();

  const isSupported = roomSettings?.data?.nextAvailableDatesEnabled === true;
  const start = fromDate ? moment(fromDate) : moment();
  const startDate = start.format(DATE_FORMAT);
  const endDate = start.clone().add(MONTHS_AHEAD, 'month').endOf('month').format(DATE_FORMAT);

  const enabled = isFlagOn && isSupported && isUnavailable && nights > 0 && !!roomCode;

  const { data, isFetching } = useRoomNextAvailableCalendar(
    { startDate, endDate, adults, children, roomCode },
    enabled
  );

  const nextStay = useMemo(() => {
    if (!enabled || !data) {
      return null;
    }

    return findNextAvailableStay(data, startDate, nights);
  }, [enabled, data, startDate, nights]);

  return { nextStay, isLoading: enabled && isFetching };
}
